/* eslint-disable dot-notation */
import { parseFromString, unescape } from './htmlParser';
import { applyColumnPercentages } from './applyColumnPercentages';
import { MIN_RENDERED_COLUMN_WIDTH, TABLE_FIT_REFERENCE_WIDTH } from '@/constants';

const RENDERED_WIDTH_PRECISION = 2;

// The smallest fraction of a table one column may take. A pixel minimum cannot work here - this module
// emits proportions, long before anything knows the container width.
const MIN_COLUMN_SHARE = MIN_RENDERED_COLUMN_WIDTH / TABLE_FIT_REFERENCE_WIDTH;

// Width sources, newest first: `data-colwidth`, legacy `colwidth`, or an inline `style="width"`.
// `parseInt` tolerates the trailing unit, so all three parse the same way.
const getCellWidthSourceValue = (cell) =>
  cell.attributes['data-colwidth']?.value ||
  cell.attributes['colwidth']?.value ||
  (cell.style && cell.style.width ? cell.style.width : null);

// A recorded zero is left alone: a merged cell's trailing `,0` means "this cell contributes nothing to
// that column", and its real width comes from another row.
const atLeastFloor = (width, floor) => (width > 0 ? Math.max(width, floor) : width);

/**
 * Floor every column at the minimum share; unsized ones get what the sized ones leave over.
 *
 * @param {number[]} widths - one entry per column; zero or non-finite means "never sized".
 * @param {number} tableWidth - the table's rendered width, or 0 before layout.
 */
export const resolveColumnWidthsForTable = (widths, tableWidth) => {
  const recorded = widths.map((width) => (Number.isFinite(width) && width > 0 ? width : 0));
  const columns = recorded.length;
  const unsizedCount = recorded.filter((width) => width === 0).length;

  // Circular: the floor is a fraction of the basis, and raising a column grows the basis.
  let basis = Math.max(
    tableWidth || 0,
    recorded.reduce((sum, width) => sum + width, 0)
  );

  for (let pass = 0; pass < 8; pass += 1) {
    const floor = columnFloorFor(basis, columns);
    const floored = recorded.map((width) => Math.max(width, floor));
    const next = Math.max(
      tableWidth || 0,
      floored.reduce((sum, width) => sum + width, 0)
    );
    const settled = Math.abs(next - basis) < 0.5;

    basis = next;

    if (settled) {
      break;
    }
  }

  const floor = columnFloorFor(basis, columns);
  const floored = recorded.map((width) => Math.max(width, floor));
  const sizedTotal = floored.reduce(
    (sum, width, index) => (recorded[index] > 0 ? sum + width : sum),
    0
  );
  // Unsized columns divide what the sized ones leave, not merely the minimum, or they are starved.
  const unsizedShare = unsizedCount ? Math.max(floor, (basis - sizedTotal) / unsizedCount) : 0;

  return recorded.map((width, index) => (width > 0 ? floored[index] : unsizedShare));
};

// Raise every width below the minimum share, taking the extra out of the others' shares.
export const floorToMinimumShare = (widths) => {
  const recorded = widths.map((width) => (Number.isFinite(width) && width > 0 ? width : 0));
  let floored = recorded;

  for (let pass = 0; pass < 8; pass += 1) {
    const total = floored.reduce((sum, width) => sum + width, 0);
    const floor = columnFloorFor(total, widths.length);
    // Re-floored from `recorded`, never the previous pass, so nothing is raised twice.
    const next = recorded.map((width) => Math.max(width, floor));
    const nextTotal = next.reduce((sum, width) => sum + width, 0);

    floored = next;

    if (Math.abs(nextTotal - total) < 0.5) {
      break;
    }
  }

  return floored;
};

// Floor applied PER COLUMN: a merged cell's colwidth carries one entry per column it spans, so
// flooring the total would leave a three-column merged cell narrower than three floored columns.
const sumColWidths = (colWidthValue, floor = 0) => {
  if (colWidthValue.includes(',')) {
    // Handling column width calculation for merged cells.
    return colWidthValue
      .split(',')
      .reduce((acc, width) => acc + atLeastFloor(parseInt(width, 10) || 0, floor), 0);
  }
  return atLeastFloor(parseInt(colWidthValue, 10) || 0, floor);
};

// Tables saved before `data-wrapperwidth`/`data-tablewidth` have neither, so derive a stand-in width
// from the table's inline width or its first row. Render-time only; stored content is untouched.
const deriveLegacyTableWidth = (table) => {
  const inlineTableWidth = table.style && table.style.width ? parseInt(table.style.width, 10) : 0;
  if (inlineTableWidth) {
    return inlineTableWidth;
  }

  const firstRow = table.rows[0];
  if (!firstRow) {
    return 0;
  }

  return Array.prototype.reduce.call(
    firstRow.children,
    (sum, cell) => {
      const colWidthValue = getCellWidthSourceValue(cell);
      return colWidthValue ? sum + sumColWidths(colWidthValue) : sum;
    },
    0
  );
};

// Under `table-layout: fixed` a table is as wide as the GREATER of its own width and its columns' sum,
// so a colgroup of 900+700+400 renders 2000px wide inside a 736px page. No CSS guard can prevent it,
// because the widths live on `col` elements. Re-expressing them as proportions of their own total fits
// the table to whatever box it is in. Kept in step with
// medusa/ce-app-api/lib/ce-sdk/lib/templateRenderer/table.js.
const normaliseColgroupWidths = (table) => {
  const colgroup = table.querySelector && table.querySelector('colgroup');

  if (!colgroup || colgroup.children.length === 0) {
    return;
  }

  const cols = Array.prototype.slice.call(colgroup.children);

  const widths = cols.map((col) => parseInt(col.style.width, 10) || 0);
  const total = widths.reduce((sum, width) => sum + width, 0);

  if (total <= 0) {
    return;
  }

  if (widths.every((width) => width > 0)) {
    // With a colgroup present these widths, not the cell widths, size the columns under fixed layout.
    applyColumnPercentages(colgroup, floorToMinimumShare(widths), RENDERED_WIDTH_PRECISION);
    return;
  }

  cols.forEach((col) => col.style.removeProperty('width'));
};

// No usable width: no attribute, or a single `0`. Treating a standalone `0` as recorded emitted
// `width: 0%` and collapsed the column. A zero PART of a merged cell's list is left alone.
const isUnsizedWidthValue = (colWidthValue) =>
  !colWidthValue || (!colWidthValue.includes(',') && (parseInt(colWidthValue, 10) || 0) <= 0);

const cellsWithoutWidth = (row) =>
  Array.prototype.filter.call(row.children, (cell) =>
    isUnsizedWidthValue(getCellWidthSourceValue(cell))
  ).length;

// Column SLOTS, not cells: a merged cell's colwidth occupies one slot per column.
const columnCount = (table) =>
  Array.prototype.reduce.call(
    table.rows,
    (widest, row) =>
      Math.max(
        widest,
        Array.prototype.reduce.call(
          row.children,
          (count, cell) => {
            const colWidthValue = getCellWidthSourceValue(cell);
            return count + (colWidthValue ? colWidthValue.split(',').length : 1);
          },
          0
        )
      ),
    0
  );

// Capped at an equal share: past ~20 columns the minimum each exceeds a whole table, and flooring then
// grows the basis, which raises the floor again - the shares ran to 122% and overflowed.
const columnFloorFor = (basis, columns) =>
  columns > 0 ? Math.min(basis * MIN_COLUMN_SHARE, basis / columns) : 0;

const recordedRowWidth = (row, floor) =>
  Array.prototype.reduce.call(
    row.children,
    (sum, cell) => {
      const colWidthValue = getCellWidthSourceValue(cell);
      return isUnsizedWidthValue(colWidthValue) ? sum : sum + sumColWidths(colWidthValue, floor);
    },
    0
  );

/**
 * What each column with NO recorded width gets, once the sized columns have taken their share.
 *
 * The reported bug lived here. A table whose sized columns already total the whole basis left
 * `basis - recorded === 0` to share out, so an unsized column was emitted at `width: 0%` - a valid
 * declaration the browser honours, collapsing the column to whatever its content forces (24px in the
 * reported case). Before `columnBasis` existed the same arithmetic went NEGATIVE, which the CSSOM
 * discarded, so the browser sized the column itself and the symptom was milder but still wrong.
 *
 * The minimum is the floor either way: an unsized column is still a column and has to be readable.
 */
// What each column with no recorded width gets. Floored, or a table whose sized columns already total
// the basis emits `width: 0%` for it and the column collapses.
const unsizedColumnWidth = (row, columnBasis, floor) => {
  const unsized = cellsWithoutWidth(row);

  if (!unsized) {
    return 0;
  }

  return Math.max(floor, (columnBasis - recordedRowWidth(row, floor)) / unsized);
};

/**
 * The widest row's own requirement: its recorded widths, plus the floor for each column it leaves
 * unsized, everything already floored.
 */
const widestRowWidth = (table, floor = 0) =>
  Array.prototype.reduce.call(
    table.rows,
    (widest, row) =>
      Math.max(widest, recordedRowWidth(row, floor) + cellsWithoutWidth(row) * floor),
    0
  );

// The width every percentage is taken against. Circular - the floor is a fraction of the basis, and
// raising columns grows the basis - so iterate to convergence, bounded. Starting unfloored keeps
// ordinary tables at exactly the width they have today.
const resolveColumnBasis = (table, tableWidth) => {
  const columns = columnCount(table);
  let basis = Math.max(tableWidth, widestRowWidth(table));

  for (let pass = 0; pass < 8; pass += 1) {
    const next = Math.max(tableWidth, widestRowWidth(table, columnFloorFor(basis, columns)));
    const settled = Math.abs(next - basis) < 0.5;

    basis = next;

    if (settled) {
      break;
    }
  }

  return basis;
};

/**
 * Put every table inside a `.pm-table-wrapper`, once. The wrapper is the scroll container, so EVERY
 * table needs one - including legacy content that recorded no widths, which went unwrapped before.
 *
 * @returns {HTMLElement} the wrapper the table now sits in.
 */
const wrapTable = (table) => {
  const parent = table.parentNode;

  if (parent.classList.contains('pm-table-wrapper')) {
    return parent;
  }

  const wrapper = document.createElement('div');
  wrapper.classList.add('pm-table-wrapper');
  parent.replaceChild(wrapper, table);
  wrapper.appendChild(table);

  return wrapper;
};

// The table's own width and the box it was saved in. Legacy content records neither, so both fall back
// to `deriveLegacyTableWidth`.
const resolveTableWidths = (table) => {
  const wrapperWidth =
    table.attributes['data-wrapperwidth'] && table.attributes['data-wrapperwidth'].value
      ? parseInt(table.attributes['data-wrapperwidth'].value, 10)
      : 0;
  const tableWidth =
    table.attributes['data-tablewidth'] && table.attributes['data-tablewidth'].value
      ? parseInt(table.attributes['data-tablewidth'].value, 10)
      : wrapperWidth;

  if (!tableWidth) {
    const derived = deriveLegacyTableWidth(table);
    return { tableWidth: derived, wrapperWidth: derived };
  }

  return { tableWidth, wrapperWidth };
};

const updateTableColumnsWidth = (content) => {
  const parsedHtml = parseFromString(content);
  const tableElements = parsedHtml.getElementsByTagName('table');

  Array.prototype.forEach.call(tableElements, (table) => {
    // Runs for every table, independently of the data-*width attributes below: a colgroup can
    // overflow the page whether or not those attributes were ever saved.
    normaliseColgroupWidths(table);

    const resolved = resolveTableWidths(table);
    const { wrapperWidth } = resolved;
    const { tableWidth } = resolved;

    const wrapper = wrapTable(table);

    if (tableWidth && wrapperWidth) {
      const relativeTableWidth = ((tableWidth * 100) / wrapperWidth).toFixed(2);
      // eslint-disable-next-line no-param-reassign
      table.style.width = `${relativeTableWidth}%`;
      // eslint-disable-next-line no-param-reassign
      table.style.minWidth = 'unset';

      const columnBasis = resolveColumnBasis(table, tableWidth);
      // In the same pixels the widths are recorded in, so that the SHARE it produces clears the
      // minimum. Raising a column costs the others a little of their share and the table still fits.
      const columnFloor = columnFloorFor(columnBasis, columnCount(table));

      // The author's width in absolute pixels; below the breakpoint CEModuleRenderer floors the table
      // at this and lets the wrapper scroll. On the WRAPPER, not the table: custom properties inherit,
      // and several suites assert the table's `style` attribute exactly.
      wrapper.style.setProperty('--pm-table-width', `${Math.round(columnBasis)}px`);

      Array.prototype.forEach.call(table.rows, (row) => {
        const cellElements = row.children;
        // Share for the columns with no recorded width, floored like any other so one can never be
        // emitted at zero. See `unsizedColumnWidth`.
        const columnWidth = unsizedColumnWidth(row, columnBasis, columnFloor);

        Array.prototype.forEach.call(cellElements, (cell) => {
          const colWidthValue = getCellWidthSourceValue(cell);

          if (!isUnsizedWidthValue(colWidthValue)) {
            // If a column width is known, set the column's width relative to the table.
            const cellWidth = sumColWidths(colWidthValue, columnFloor);
            const relativeCellWidth = ((cellWidth * 100) / columnBasis).toFixed(2);
            // eslint-disable-next-line no-param-reassign
            cell.style.width = `${relativeCellWidth}%`;
          } else {
            // If column width has not been set explicitly, set remaining columns to equal width.
            const relativeCellWidth = ((columnWidth * 100) / columnBasis).toFixed(2);
            // eslint-disable-next-line no-param-reassign
            cell.style.width = `${relativeCellWidth}%`;
          }
        });
      });
    }
  });

  return unescape(parsedHtml.body.innerHTML);
};

export default updateTableColumnsWidth;
